'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payloadStr]) => {
  const payload = typeof payloadStr === 'string' ? JSON.parse(payloadStr) : payloadStr;
  try {
    const response = await fleetApi.post(url + getTzParam(), payload);
    return response;
  } catch (err) {
    console.error('API fetcher error:', { url, payload, status: err?.response?.status, data: err?.response?.data });
    throw err;
  }
};

export const revalidateMasterFuelPriceList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/master-fuel-price/search');
};

/**
 * Searchable fields = columns shown in MasterFuelPricePage table.
 * key -> DB column (+ joined table) used by backend search.
 */
export const MASTER_FUEL_PRICE_SEARCH_FIELDS = {
  company_name: { name: 'name', table_name: 'm_company' },
  fuel_type_name: { name: 'name', table_name: 'm_fuel_type' },
  fuel_type_code: { name: 'code', table_name: 'm_fuel_type' },
  fuel_type_category: { name: 'category', table_name: 'm_fuel_type' },
  fuel_type_uom: { name: 'uom_type', table_name: 'm_fuel_type' },
  description: { name: 'description' },
  price: { name: 'price' },
  valid_at: { name: 'valid_at' }
};

const EMPTY_COLUMN = { logic_operator: 'like', name: 'id', operator: 'AND', value: '' };

export const buildMasterFuelPriceSearchPayload = ({
  start = 0,
  length = 1000,
  searchKey = 'all',
  searchValue = '',
  companyId = ''
} = {}) => {
  const fb = new FilterBuilder()
    .setPaging(start, length)
    .addJoin('company', ['name'])
    .addJoin('fuel_type', ['id', 'name', 'code', 'category', 'description', 'uom_type'])
    .setOrder(['id'], false);

  if (companyId) {
    const idStr = String(companyId);
    if (idStr.includes(',')) fb.in('company_id', idStr);
    else fb.equal('company_id', idStr);
  }

  const query = String(searchValue ?? '').trim();
  if (query) {
    if (!searchKey || searchKey === 'all') {
      // OR search across every shown column (each column only once)
      Object.values(MASTER_FUEL_PRICE_SEARCH_FIELDS).forEach((f) => fb.addFilter(f.name, query, f.table_name ?? null));
    } else {
      const f = MASTER_FUEL_PRICE_SEARCH_FIELDS[searchKey];
      if (f) fb.like(f.name, query, 'AND', f.table_name ?? null);
    }
  }

  const built = fb.build();
  if (!built.columns || built.columns.length === 0) built.columns = [{ ...EMPTY_COLUMN }];
  return built;
};

const extractList = (resp) => {
  const body = resp ?? {};
  const rows = Array.isArray(body?.data) ? body.data : Array.isArray(body?.data?.data) ? body.data.data : Array.isArray(body) ? body : [];
  const total = body?.totalRecord ?? body?.data?.totalRecord ?? body?.totalRecords ?? rows.length;
  return { rows, total: Number(total) || 0 };
};

export const fetchAllMasterFuelPrice = async ({ searchKey, searchValue, companyId, total = 0 } = {}) => {
  const payload = buildMasterFuelPriceSearchPayload({ start: 0, length: total > 0 ? total : 9999, searchKey, searchValue, companyId });
  const resp = await fleetApi.post('/master-fuel-price/search' + getTzParam(), payload);
  return extractList(resp).rows;
};

export const useMasterFuelPriceList = (page = 0, rowsPerPage = 1000, searchKey = 'all', searchValue = '', companyId = '') => {
  const payload = useMemo(
    () => buildMasterFuelPriceSearchPayload({ start: page * rowsPerPage, length: rowsPerPage, searchKey, searchValue, companyId }),
    [page, rowsPerPage, searchKey, searchValue, companyId]
  );

  const key = useMemo(() => ['/master-fuel-price/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  const { rows, total } = extractList(data);

  return {
    data: rows,
    totalRecord: total,
    isLoading,
    isError: error
  };
};

export const getMasterFuelPriceById = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const resp = await fleetApi.get(`/master-fuel-price/${id}${getTzParam()}`);
    if (resp?.data) return resp.data;

    // Fallback using search and FilterBuilder if direct GET fails or needs complex join
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).filterByCompany().equal('id', String(id));
    const payload = fb.build();
    const response = await fleetApi.post(`/master-fuel-price/search${getTzParam()}`, payload);
    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);

    return Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
  } catch (err) {
    console.error('Failed to fetch master fuel price by id:', err);
    throw err;
  }
};

export const createMasterFuelPrice = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/master-fuel-price/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateMasterFuelPriceList();
    return response.data;
  } catch (err) {
    console.error('Error creating master fuel price:', err);
    throw err;
  }
};

export const updateMasterFuelPrice = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/master-fuel-price/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateMasterFuelPriceList();
    mutate(`/master-fuel-price/${id}`);
    return response.data;
  } catch (err) {
    console.error('Error updating master fuel price:', err);
    throw err;
  }
};

export const deleteMasterFuelPrice = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/master-fuel-price/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateMasterFuelPriceList();
    return response.data;
  } catch (err) {
    console.error('Error deleting master fuel price:', err);
    throw err;
  }
};
